import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  customFormatsApi,
  qualityApi,
  type CustomFormat,
  type QualityMediaKind,
  type QualityProfileResponse,
} from "@/api/quality";
import { Button } from "@/components/ui/Button";
import { Checkbox, Field, Select } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";

/**
 * Format a size in MB as a human-friendly string. Numbers shown are
 * "what you'd actually see on disk" estimates — gut feel, not min/max.
 *
 *   < 1000 MB → "640 MB" (no decimal — these are noisy)
 *   ≥ 1000 MB → "1.2 GB" / "12 GB" (1 decimal up to 9.9 GB, integer above)
 */
function formatSizeMb(mb: number): string {
  if (mb < 1000) return `${mb} MB`;
  const gb = mb / 1024;
  return gb < 10 ? `${gb.toFixed(1)} GB` : `${Math.round(gb)} GB`;
}

const KIND_DESCRIPTIONS: Record<QualityMediaKind, string> = {
  movie: "Pick which movie release qualities to accept. Sizes are gut-feel for a typical ~120 min feature.",
  tv: "Pick which TV release qualities to accept. Sizes are per-episode (~45 min).",
  music: "Pick which audio formats and bitrates to accept. Sizes are per album (~45 min).",
};

export function Quality() {
  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Settings"
        title="Quality"
        description="Pick which release qualities to accept and where to stop seeking upgrades — per media kind. The estimated size column gives you a sense of disk impact before enabling."
      />
      <ProfileSection kind="movie" title="Movies" />
      <ProfileSection kind="tv" title="TV" />
      <ProfileSection kind="music" title="Music" />
    </div>
  );
}

interface SectionProps {
  kind: QualityMediaKind;
  title: string;
}

function ProfileSection({ kind, title }: SectionProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["quality", kind],
    queryFn: () => qualityApi.getProfile(kind),
  });

  return (
    <section className="overflow-hidden rounded-md border border-line bg-surface/30">
      <header className="flex items-center justify-between border-b border-line bg-surface-2/50 px-5 py-3">
        <h2 className="font-display text-lg font-medium tracking-display-tight">
          {title}
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/45">
          {KIND_DESCRIPTIONS[kind]}
        </span>
      </header>
      <div className="p-5">
        {isLoading || !data ? (
          <Skeleton.Rows count={6} />
        ) : (
          <ProfileEditor
            data={data}
            onSaved={() => {
              toast(`${title} quality profile saved`, "success");
              qc.invalidateQueries({ queryKey: ["quality", kind] });
            }}
          />
        )}
      </div>
    </section>
  );
}

interface EditorProps {
  data: QualityProfileResponse;
  onSaved: () => void;
}

function ProfileEditor({ data, onSaved }: EditorProps) {
  const { toast } = useToast();
  // Working copy — re-seeded from server data on (re)load.
  const [allowed, setAllowed] = useState<Set<string>>(new Set(data.allowed));
  const [cutoff, setCutoff] = useState<string | null>(data.cutoff);
  const [upgradeAllowed, setUpgradeAllowed] = useState(data.upgrade_allowed);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setAllowed(new Set(data.allowed));
    setCutoff(data.cutoff);
    setUpgradeAllowed(data.upgrade_allowed);
  }, [data]);

  // Catalog is best → worst; preserving order matters because the API
  // expects `allowed` in user-preferred order. We keep that == catalog
  // order in v1 (no manual reorder UI yet).
  const allowedInOrder = data.catalog
    .filter((q) => allowed.has(q.slug))
    .map((q) => q.slug);

  const cutoffOptions = data.catalog.filter((q) => allowed.has(q.slug));

  // Running total for "you've selected X qualities, est total ___ if you
  // grab one of every quality" — gives a quick sense of upgrade churn.
  const enabledTotal = data.catalog
    .filter((q) => allowed.has(q.slug))
    .reduce((acc, q) => acc + q.typical_size_mb, 0);
  // The realistic "what you'll actually keep" is the cutoff size (or
  // the largest enabled quality if no cutoff). That's the size of the
  // final file once upgrades settle.
  const cutoffQuality = cutoff
    ? data.catalog.find((q) => q.slug === cutoff)
    : null;
  const settledSize =
    cutoffQuality ??
    data.catalog
      .filter((q) => allowed.has(q.slug))
      .reduce<typeof data.catalog[number] | null>((best, q) =>
        best === null || q.typical_size_mb > best.typical_size_mb ? q : best,
      null);

  const toggle = (slug: string) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
    // If we just unchecked the cutoff, clear it.
    if (cutoff === slug) setCutoff(null);
  };

  const saveMut = useMutation({
    mutationFn: () =>
      qualityApi.putProfile(data.media_kind, {
        allowed: allowedInOrder,
        cutoff,
        upgrade_allowed: upgradeAllowed,
      }),
    onSuccess: onSaved,
    onError: (e) => toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      if (cutoff && !allowed.has(cutoff)) {
        toast("Cutoff must be one of the enabled qualities", "error");
        return;
      }
      await saveMut.mutateAsync();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="overflow-x-auto rounded-md border border-line/70 bg-surface/40">
        <table className="w-full text-sm">
          <thead className="bg-surface-2/40">
            <tr className="text-left">
              <th className="w-14 px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Allow
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Quality
              </th>
              <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Source
              </th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                {data.media_kind === "music" ? "Bitrate" : "Resolution"}
              </th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                Est. size
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {data.catalog.map((q) => {
              const isCutoff = q.slug === cutoff;
              return (
                <tr
                  key={q.slug}
                  className={
                    "transition hover:bg-surface-2/40" +
                    (isCutoff ? " bg-accent/[0.06]" : "")
                  }
                >
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={allowed.has(q.slug)}
                      onChange={() => toggle(q.slug)}
                      aria-label={`Allow ${q.name}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {q.name}
                    {isCutoff && (
                      <span className="ml-2 rounded-full border border-accent/40 bg-accent/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-accent">
                        cutoff
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-foreground/65">{q.source}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-foreground/65">
                    {q.lossless
                      ? "lossless"
                      : q.bitrate_kbps
                        ? `${q.bitrate_kbps} kbps`
                        : q.resolution
                          ? `${q.resolution}p`
                          : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-foreground/85">
                    {formatSizeMb(q.typical_size_mb)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {allowed.size > 0 && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-line/60 bg-surface/30 px-4 py-2.5 text-xs">
          <span className="text-foreground/55">
            <span className="font-mono tabular-nums text-foreground/85">
              {allowed.size}
            </span>{" "}
            quality
            {allowed.size === 1 ? "" : "s"} enabled
          </span>
          {settledSize && (
            <span className="text-foreground/55">
              Final-file size{" "}
              <span className="font-mono tabular-nums text-foreground/85">
                ≈ {formatSizeMb(settledSize.typical_size_mb)}
              </span>
              <span className="ml-1 text-foreground/40">
                ({cutoff ? "at cutoff" : "best enabled"})
              </span>
            </span>
          )}
          {allowed.size > 1 && (
            <span className="text-foreground/55">
              All-quality bandwidth ceiling{" "}
              <span className="font-mono tabular-nums text-foreground/85">
                ≈ {formatSizeMb(enabledTotal)}
              </span>
              <span className="ml-1 text-foreground/40">
                (if you grabbed one of every enabled quality)
              </span>
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-5">
        <Field
          label="Cutoff"
          hint="Once you have a release at or above this quality, stop searching for upgrades. Leave unset to always upgrade."
        >
          <Select
            value={cutoff ?? ""}
            onChange={(e) => setCutoff(e.target.value || null)}
          >
            <option value="">— no cutoff —</option>
            {cutoffOptions.map((q) => (
              <option key={q.slug} value={q.slug}>
                {q.name} ({formatSizeMb(q.typical_size_mb)})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Upgrades" hint="Replace existing files when a better release shows up.">
          <label className="flex h-[42px] items-center gap-2 rounded-md border border-line bg-background/40 px-3 text-sm">
            <Checkbox
              checked={upgradeAllowed}
              onChange={(e) => setUpgradeAllowed(e.target.checked)}
            />
            Allow upgrades
          </label>
        </Field>
      </div>

      <CustomFormatsAttachment profileId={data.id} mediaKind={data.media_kind} />

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <Button onClick={onSubmit} loading={submitting}>
          Save
        </Button>
      </div>
    </div>
  );
}

interface AttachmentProps {
  profileId: number;
  mediaKind: QualityMediaKind;
}

/**
 * Custom-format multi-select. Lets the user attach format rows to this
 * profile; the grab selector uses the attached formats to score
 * candidate releases (issue #2). The attachment list saves
 * independently of the rest of the profile (its own button) — the
 * underlying endpoint is `PUT /api/v1/quality-profiles/:id/formats`,
 * which replaces the full attachment set in one call.
 */
function CustomFormatsAttachment({ profileId, mediaKind }: AttachmentProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const formatsQuery = useQuery({
    queryKey: ["custom-formats"],
    queryFn: customFormatsApi.list,
  });
  const attachedQuery = useQuery({
    queryKey: ["quality-profile-formats", profileId],
    queryFn: () => customFormatsApi.listForProfile(profileId),
    enabled: profileId > 0,
  });

  const [picked, setPicked] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (attachedQuery.data) {
      setPicked(new Set(attachedQuery.data.format_ids));
    }
  }, [attachedQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      customFormatsApi.setForProfile(profileId, Array.from(picked)),
    onSuccess: () => {
      toast("Custom formats attached", "success");
      qc.invalidateQueries({ queryKey: ["quality-profile-formats", profileId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const eligible = (formatsQuery.data ?? []).filter(
    (f: CustomFormat) => f.media_kind === mediaKind,
  );

  const toggle = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dirty =
    attachedQuery.data &&
    (picked.size !== attachedQuery.data.format_ids.length ||
      Array.from(picked).some(
        (id) => !attachedQuery.data!.format_ids.includes(id),
      ));

  return (
    <div className="space-y-3 rounded-md border border-line/60 bg-surface/30 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-sm font-medium tracking-display-tight">
            Custom formats
          </h3>
          <p className="text-xs text-foreground/55">
            Regex-scored release types. Attached formats add or subtract
            score from matching releases — applied as a tie-break after
            quality rank.{" "}
            <Link
              to="/settings/quality/custom-formats"
              className="text-accent/85 underline-offset-2 hover:underline"
            >
              Manage catalog →
            </Link>
          </p>
        </div>
      </div>
      {formatsQuery.isLoading || attachedQuery.isLoading ? (
        <Skeleton.Rows count={2} />
      ) : eligible.length === 0 ? (
        <p className="text-xs text-foreground/55">
          No custom formats defined for this media kind yet.
        </p>
      ) : (
        <>
          <ul className="space-y-1">
            {eligible.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-surface-2/40"
              >
                <label className="flex flex-1 items-center gap-2 text-sm">
                  <Checkbox
                    checked={picked.has(f.id)}
                    onChange={() => toggle(f.id)}
                    aria-label={`Attach ${f.name}`}
                  />
                  <span className="font-medium">{f.name}</span>
                  <span className="font-mono text-[11px] text-foreground/55">
                    {f.pattern}
                  </span>
                </label>
                <span
                  className={
                    "font-mono text-xs tabular-nums " +
                    (f.score > 0
                      ? "text-good"
                      : f.score < 0
                        ? "text-bad"
                        : "text-foreground/55")
                  }
                >
                  {f.score > 0 ? `+${f.score}` : f.score}
                </span>
              </li>
            ))}
          </ul>
          {dirty && (
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                onClick={() => saveMut.mutate()}
                loading={saveMut.isPending}
              >
                Save attachments
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
